import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";

const PRESETS = [["7d", 6], ["30d", 29], ["60d", 59]];
const iso = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(d);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

function Kpi({ label, value, testid, tint = "#0F172A" }) {
  return (
    <div data-testid={testid} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 140 }}>
      <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 28, fontWeight: 700, color: tint, lineHeight: 1 }}>{value}</span>
    </div>
  );
}
const CARD = { background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: 14 };

function CallsByDay({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.calls));
  const ref = useRef(null);
  return (
    <div style={CARD}>
      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 8 }}>Calls by day</div>
      <div ref={ref} data-testid="cd-calls-by-day" style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140, minWidth: rows.length * 34 }}>
          {rows.map((r) => (
            <div key={r.date} title={`${r.date}: ${r.calls} calls, ${r.connected} connected`} style={{ flex: "0 0 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <span style={{ fontSize: 9, color: "#64748b" }}>{r.calls || ""}</span>
              <div style={{ width: 18, height: `${(r.calls / max) * 100}px`, background: "#bfdbfe", borderRadius: 3, position: "relative" }}>
                <div style={{ position: "absolute", bottom: 0, width: "100%", height: r.calls ? `${(r.connected / r.calls) * 100}%` : 0, background: "#2563eb", borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 9, color: "#94a3b8", whiteSpace: "nowrap" }}>{r.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Breakdown({ title, data }) {
  const rows = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map(([, v]) => v));
  return (
    <div style={CARD}>
      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 8 }}>{title}</div>
      {rows.length === 0 ? <div style={{ fontSize: 11, color: "#94a3b8" }}>No calls in range.</div> : rows.map(([k, v]) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, fontSize: 11 }}>
          <div style={{ width: 170 }}>{k}</div><div style={{ flex: 1, height: 7, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: `${(v / max) * 100}%`, height: "100%", background: "#2563eb", borderRadius: 4 }} /></div><div style={{ width: 28, textAlign: "right" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ title, rows, testid }) {
  return (
    <div style={CARD} data-testid={testid}>
      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 8 }}>{title}</div>
      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
        <thead><tr style={{ color: "#64748b", textAlign: "left" }}>{["Name", "Calls", "Connected", "Connect rate", "Unique clients"].map((x) => <th key={x} style={{ padding: 5, fontWeight: 500 }}>{x}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id} style={{ borderTop: "0.5px solid #f1f5f9" }}><td style={{ padding: 5 }}>{r.name}</td><td>{r.calls}</td><td>{r.connected}</td><td>{r.connect_rate}%</td><td>{r.unique_clients}</td></tr>)}
          {rows.length === 0 && <tr><td colSpan={5} style={{ padding: 8, color: "#94a3b8" }}>No calls in range.</td></tr>}</tbody>
      </table>
    </div>
  );
}

/** Clients Dialer analytics — client calls only. */
export default function ClientDialerAnalytics({ opts }) {
  const { token } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    axios.get(`${API}/client-dialer/campaigns`, { params: { include_archived: true }, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCampaigns(r.data.campaigns)).catch(() => {});
  }, [token]);
  const [range, setRange] = useState({ from: daysAgo(6), to: iso(new Date()) });
  const [rep, setRep] = useState("");
  const [camp, setCamp] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/client-dialer/analytics`, { params: { date_from: range.from, date_to: range.to, rep_id: rep || undefined, campaign_id: camp || undefined }, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [range, rep, camp, token]);
  const setCustom = (k, v) => {
    const next = { ...range, [k]: v };
    const span = (new Date(next.to) - new Date(next.from)) / 86400000;
    if (span > 59) { const s = new Date(next.to); s.setDate(s.getDate() - 59); next.from = iso(s); }
    setRange(next);
  };
  const t = d?.totals || {};
  const sel = { fontSize: 12, padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0" };
  return (
    <div data-testid="cd-analytics" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {PRESETS.map(([l, n]) => <button key={l} data-testid={`cd-range-${l}`} className="btn" onClick={() => setRange({ from: daysAgo(n), to: iso(new Date()) })}>{l}</button>)}
        <input data-testid="cd-range-from" type="date" value={range.from} onChange={(e) => setCustom("from", e.target.value)} style={sel} />
        <input data-testid="cd-range-to" type="date" value={range.to} onChange={(e) => setCustom("to", e.target.value)} style={sel} />
        <span style={{ fontSize: 10, color: "#94a3b8" }}>Custom ranges up to 60 days</span>
        <select data-testid="cd-an-rep" value={rep} onChange={(e) => setRep(e.target.value)} style={sel}><option value="">All reps</option>{(opts.reps || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <select data-testid="cd-an-campaign" value={camp} onChange={(e) => setCamp(e.target.value)} style={sel}><option value="">All campaigns</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.status === "archived" ? " (archived)" : ""}</option>)}</select>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Kpi testid="cd-kpi-calls" label="Calls" value={t.calls ?? "—"} tint="#2563EB" /><Kpi testid="cd-kpi-connected" label="Connected" value={t.connected ?? "—"} tint="#1D9E75" />
        <Kpi testid="cd-kpi-rate" label="Connect rate" value={d ? `${t.connect_rate}%` : "—"} tint="#EA8A1A" /><Kpi testid="cd-kpi-unique" label="Unique clients" value={t.unique_clients ?? "—"} />
        <Kpi testid="cd-kpi-demos" label="Demos booked" value={t.demos_booked ?? "—"} tint="#7C3AED" />
      </div>
      <CallsByDay rows={d?.by_day || []} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Table testid="cd-by-campaign" title="By campaign" rows={d?.by_campaign || []} />
        <Table testid="cd-by-rep" title="By rep" rows={d?.by_rep || []} />
      </div>
      <div data-testid="cd-outcomes" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Breakdown title="Contact status" data={d?.outcomes?.contact_status} /><Breakdown title="Interest level" data={d?.outcomes?.interest_level} />
        <Breakdown title="Next action" data={d?.outcomes?.next_action} /><Breakdown title="Products pitched" data={d?.outcomes?.products_pitched} />
      </div>
    </div>
  );
}
